import React, { useMemo, useState } from 'react';
import { useAlert } from 'react-alert';
import { useForm } from 'react-hook-form';
import LoaderWrapper from '../../Components/LoaderWrapper/LoaderWrapper';
import { RouterConstant } from '../../constants/RouteConstant';
import { getDateFromNo } from '../../helper/dateData';
import { submitSalaryAdvance, submitSalaryTopup } from '../../API/Services/requestService';

const NEEDS = [
  { label: 'Feeding', code: 'feeding' },
  { label: 'Transportation', code: 'transportation' },
  { label: 'Emergency', code: 'emergency' },
  { label: 'Medical Needs', code: 'medical_needs' },
  { label: 'Work', code: 'work' },
  { label: 'Family', code: 'family' },
  { label: 'Other', code: 'other' },
];

// Kept as-is (not used now, but harmless if you keep it)
const DURATION_OPTIONS = [1, 2, 3, 4, 5, 6];

function formatDateMaybe(value) {
  if (typeof value === 'number') return getDateFromNo(value);
  const date = new Date(value);
  if (!isNaN(date.getTime())) {
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return String(value ?? '');
}

const ReviewRequest = ({
  toggleReviewRequest,
  interestAmount,
  requestedAmount,
  salaryDueDate,
  loanType = 'salaryAdvance',
  duration, // ← previously selected duration comes from props
  totalAmountToBePaid,
  paymentBreakdown = [],
}) => {
  const alert = useAlert();
  const [loader, setLoader] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      duration: duration, // kept in form state but no dropdown now
    }
  });

  const dueDateText = useMemo(() => formatDateMaybe(salaryDueDate), [salaryDueDate]);
  const totalToPay = useMemo(
    () => Number(totalAmountToBePaid ?? (Number(requestedAmount) + Number(interestAmount))),
    [totalAmountToBePaid, requestedAmount, interestAmount]
  );

  const needCode = watch('needCode');
  const otherText = watch('otherText', '');
  const agreedToTerms = watch('checkTerms');

  const canSubmit = useMemo(() => {
    if (!agreedToTerms || !needCode) return false;
    if (needCode === 'other' && !otherText?.trim()) return false;
    return true;
  }, [needCode, otherText, agreedToTerms]);

  const onSubmit = async (data) => {
    if (!canSubmit) return;
    const need = data.needCode === 'other' ? data.otherText.trim() : data.needCode;
    setLoader(true);

    const payload = {
      requestedAmount: Number(requestedAmount),
      interestAmount: Number(interestAmount),
      salaryDueDate: String(salaryDueDate),
    };

    try {
      const res = loanType === 'salaryTopUp'
        // Use the previously selected duration from props (read-only)
        ? await submitSalaryTopup({ ...payload, duration: Number(duration), need })
        : await submitSalaryAdvance({ ...payload, need });

      setLoader(false);
      if (res?.data?.status === 'OK') {
        alert.success(res?.data?.message);
        setSubmitted(true);
      } else {
        alert.error(res?.data?.message || 'Something went wrong');
      }
    } catch (err) {
      setLoader(false);
      alert.error(err?.message || 'Network error');
    }
  };

  if (loader) return <LoaderWrapper />;

  if (submitted) {
    return (
      <div className="request-page-container">
        <div className="request-card confirmation-view">
          <div className="confirmation-icon-wrapper">
            <div className="css-tick"></div>
          </div>
          <h2 className="request-heading">Request Submitted!</h2>
          <p className="request-subheading">Your request is now pending approval. You can track its status in your transaction history.</p>
          <div className="form-actions">
            <a className="btn btn-primary w-100" href={RouterConstant.Layout}>Back to Dashboard</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="request-page-container">
      <div className="request-card review-card">
        <h2 className="request-heading">Review Your Request</h2>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="review-details">
            <div className="detail-item">
              <span>Loan Type</span>
              <strong>{loanType === 'salaryTopUp' ? 'Salary Top-Up' : 'Salary Advance'}</strong>
            </div>

            <div className="detail-item">
              <span>Amount Requested</span>
              <strong>₦{Number(requestedAmount).toLocaleString()}</strong>
            </div>

            <div className="detail-item">
              <span>Processing Fee</span>
              <strong>₦{Number(interestAmount).toLocaleString()}</strong>
            </div>

            {loanType === 'salaryTopUp' && (
              <>
                {/* Duration is read-only (previously selected), not a dropdown */}
                <div className="detail-item">
                  <span>Duration</span>
                  <strong>
                    {duration ? `${Number(duration)} ${Number(duration) === 1 ? 'Month' : 'Months'}` : '—'}
                  </strong>
                </div>

                <div className="detail-item total">
                  <span>Total Repayment</span>
                  <strong>₦{totalToPay.toLocaleString()}</strong>
                </div>
              </>
            )}

            <div className="detail-item">
              <span>Due Date</span>
              <strong>{dueDateText}</strong>
            </div>
          </div>

          {loanType === 'salaryTopUp' && paymentBreakdown?.length > 0 && (
            <div className="repayment-breakdown">
              <h3 className="breakdown-title">Repayment Schedule</h3>
              <ul className="breakdown-list">
                {paymentBreakdown.map((p, idx) => (
                  <li key={idx}>
                    <span>{formatDateMaybe(p?.dueDate)}</span>
                    <span className="amount">₦{Number(p?.amount).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="input-group">
            <label htmlFor="needCode" className="form-label">Reason for this request</label>
            <select
              id="needCode"
              className={`form-control ${errors.needCode ? 'is-invalid' : ''}`}
              style={{ width: '100%', minHeight: '45px', padding: '0 10px' }}
              {...register('needCode', { required: 'Please select a reason' })}
              defaultValue=""
            >
              <option value="" disabled>Select a reason...</option>
              {NEEDS.map(n => <option key={n.code} value={n.code}>{n.label}</option>)}
            </select>
            {errors.needCode && <small className="error-msg">{errors.needCode.message}</small>}
          </div>

          {watch('needCode') === 'other' && (
            <div className="input-group">
              <label htmlFor="otherText" className="form-label">Please specify</label>
              <textarea
                id="otherText"
                className="form-control"
                rows={3}
                placeholder="Type your reason here..."
                style={{ width: '100%', padding: '10px' }}
                {...register('otherText')}
              />
            </div>
          )}

          <div className="terms-agreement">
            <input
              type="checkbox"
              id="checkTerms"
              {...register('checkTerms', { required: true })}
            />
            <label htmlFor="checkTerms">
              I agree to pay Incash <strong>₦{totalToPay.toLocaleString()}</strong> by <strong>{dueDateText}</strong>.
            </label>
            {errors.checkTerms && <small className="error-msg full-width">You must agree to the terms to continue.</small>}
          </div>

          <div className="form-actions-row">
            <button type="button" className="btn btn-secondary" onClick={toggleReviewRequest}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>Agree & Submit</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewRequest;
